// frontend/src/services/driverService.js
// Responsibility: Centralize driver-related API calls for the app
// (listing, creating, photo upload/delete, banned drivers, etc.)

import axios from 'axios';

function normalizeList(data) {
  if (Array.isArray(data)) return data;
  if (Array.isArray(data?.rows)) return data.rows;
  if (Array.isArray(data?.drivers)) return data.drivers;
  return [];
}

// ✅ Request coalescing (prevents duplicate network calls during mount→remount bursts)
const inFlight = {
  agencyStats: null,
  agencyActive: new Map(), // key by search string
};


/**
 * Fetches drivers for the current agency, with optional filters.
 * LEGACY: Uses /agencies/drivers?status=... (kept for compatibility while migrating)
 *
 * @param {Object}   [opts]
 * @param {'Active'|'Reserved'|'Banned'} [opts.status] – status to filter by
 * @param {number}   [opts.agencyId]                   – agency to filter by
 * @param {string}   [opts.search]                     – partial name match
 * @returns {Promise<Driver[]>}
 */

// ✅ Request coalescing + short TTL cache (prevents duplicate stats calls during mount→remount bursts)
let agencyStatsCache = null; // { promise, ts }

export function fetchAgencyDriverStats() {
  const now = Date.now();

  // If a recent stats request happened very recently, reuse it
  if (agencyStatsCache && (now - agencyStatsCache.ts) < 500) {
    return agencyStatsCache.promise;
  }

  const promise = axios
    .get('/agencies/me/drivers/stats')
    .then((res) => res.data);

  agencyStatsCache = { promise, ts: now };

  // Clear cache after TTL window (not immediately)
  promise.finally(() => {
    setTimeout(() => {
      // only clear if it's still the same promise
      if (agencyStatsCache?.promise === promise) {
        agencyStatsCache = null;
      }
    }, 500);
  });

  return promise;
}



// POST /agencies/me/assignment-requests
export async function requestAgencyAssignment({ driverId, siteId, ...extra } = {}) {
  if (!driverId || !siteId) {
    throw new Error('requestAgencyAssignment requires driverId and siteId');
  }

  const { data } = await axios.post('/agencies/me/assignment-requests', {
    driver_id: driverId,
    site_id: siteId,

    // ✅ DB requires NOT NULL; canonical type in your system is 'SHIFT'
    assignment_type: 'SHIFT',

    // allow callers to override if they pass assignment_type explicitly
    ...extra,
  });

  return data;
}

// --- NEW: Agency "me" reads (assignment-derived membership) ---

/**
 * Drivers that are not currently Active/Reserved/Banned (i.e. available to be used again).
 * GET /agencies/me/drivers/available
 */
export async function fetchAgencyAvailableDrivers({ search } = {}) {
  const params = {};
  if (search) params.search = search;

  const { data } = await axios.get('/agencies/me/drivers/available', { params });
  return normalizeList(data);
}

/**
 * GET /agencies/me/drivers/active
 */
export function fetchAgencyActiveDrivers({ search } = {}) {
  const key = (search ?? '').trim().toLowerCase();

  // If already fetching this exact variant, return same promise
  const existing = inFlight.agencyActive.get(key);
  if (existing) return existing;

  const params = {};
  if (search) params.search = search;

  const p = axios
    .get('/agencies/me/drivers/active', { params })
    .then(({ data }) => normalizeList(data))
    .finally(() => {
      inFlight.agencyActive.delete(key);
    });

  inFlight.agencyActive.set(key, p);
  return p;
}


/**
 * GET /agencies/me/drivers/reserved
 */
export async function fetchAgencyReservedDrivers({ search } = {}) {
  const params = {};
  if (search) params.search = search;

  const { data } = await axios.get('/agencies/me/drivers/reserved', { params });
  return normalizeList(data);
}

/**
 * GET /agencies/me/drivers/banned
 * Canonical agency banned read. No fallback to legacy status-based lists.
 */
export async function fetchAgencyBannedDrivers({ search } = {}) {
  const params = {};
  if (search) params.search = search;

  const { data } = await axios.get('/agencies/me/drivers/banned', { params });
  return normalizeList(data);
}

/**
 * Adds a new driver under the current agency.
 * FIX: Prefer /agencies/me/drivers so agency_id is implied by the JWT and not required in body.
 *
 * @param {{ name: string, vehicle_registration_number: string, phone?: string, email?: string }} driverData
 * @returns {Promise<Driver>}
 */
export async function addDriver(driverData) {
  // Prefer the new path that does not require agency_id
  try {
    const { data } = await axios.post('/agencies/me/drivers', driverData);
    return data;
  } catch (e) {
    // Fallback to legacy path if /me route isn't mounted yet
    // (Keeps the app working while backend deploy catches up.)
    const { data } = await axios.post('/agencies/drivers', driverData);
    return data;
  }
}

/**
 * Fetches the UPS-specific banned list, with optional filters.
 * @param {Object} [opts]
 * @param {number} [opts.agencyId]
 * @param {string} [opts.search]
 * @returns {Promise<DriverBan[]>}
 */
export async function fetchBannedDrivers({ agencyId, search } = {}) {
  const params = {};
  if (agencyId) params.agency_id = agencyId;
  if (search) params.search = search;

  const { data } = await axios.get('/ups/drivers/banned', { params });
  return data;
}

/**
 * Upload or replace a driver's photo.
 * Backend endpoint:
 *   POST /ups/drivers/:id/photo
 *
 * Expected response:
 *   { message: 'Photo uploaded', driver, version }
 *
 * We return ONLY data.driver so Redux thunks/components get the updated
 * driver object directly and can do upsertOne/upsertDriver cleanly.
 */
export async function uploadDriverPhoto(id, file) {
  const formData = new FormData();
  formData.append('photo', file);

  const { data } = await axios.post(`/ups/drivers/${id}/photo`, formData, {
    headers: {
      'Content-Type': 'multipart/form-data',
    },
  });

  if (!data || !data.driver) {
    console.warn('[driverService.uploadDriverPhoto] unexpected response:', data);
    return data?.driver ?? data;
  }

  return data.driver;
}

/**
 * Delete a driver's photo.
 * Backend endpoint:
 *   DELETE /ups/drivers/:id/photo
 *
 * Expected response:
 *   { message: 'Photo removed', driver, version }
 */
export async function deleteDriverPhoto(id) {
  const { data } = await axios.delete(`/ups/drivers/${id}/photo`);

  if (!data || !data.driver) {
    console.warn('[driverService.deleteDriverPhoto] unexpected response:', data);
    return data?.driver ?? data;
  }

  return data.driver;
}
